import { GearItem, LevelPhase, LevelSim, RARITY_IDS, StarMask, chestEssence, dailyScore, starCount } from "@highmarch/domain";
import { chestReveal } from "./chestReveal";
import { btnHtml, fmtCompact, motionOk, ribbonHtml, statHtml, tickNumber } from "./kit";
import { icon } from "./iconsKit";
import { uiUrl } from "./portraits";
import { ChestStage } from "../render/chestStage";
import { lib } from "../render/views/common";
import { starConditions } from "./hub/levelFacts";
import { guide } from "./guide/guide";
import type { GuideSignal } from "./guide/guide";
import { EndScreenFacts, features } from "../features/registry";
import { TowerDamageRow, topTowersHtml } from "./damageReport";
import type { ShareOutcome } from "./shareCard";
import { challengeCrestHtml } from "./modes/challengeEnd";
import { toast } from "./toast";
import { paintedIconHtml } from "./featureIcons";
import { eventChoicesHtml, eventCrestHtml } from "./modes/eventRunEnd";
import { mainRest } from "../render/mainRest";

export type LevelEndChoice = "retry" | "next" | "levels" | "checkpoint";

/** Opens (banks) the chest and returns the gear it held — called once, when the player taps OPEN. */
export type ChestOpener = () => GearItem[];

/**
 * The run's stars (sim/stars.ts `runStars`: win, fortress at the level's ★2 HP line, the level's challenge) and the level's best before
 * this run, for "NEW BEST 2→3★". Null (a quit, a preview) shows no stars.
 */
export interface EndStars { readonly run: StarMask; readonly before: StarMask }

/**
 * Level-end timeline (art wow pass, ui.md F5), ms after the screen appears. The ribbon drops first, the stars slam one
 * by one (each earned slam shakes the screen 3 px and throws a spark ring), the stats count up under them, and the
 * chest block rises last with its 3D chest already rattling. Unearned stars fade in grey and don't slam.
 */
const END_T = { stars: [350, 600, 850], stats: 900, statsMs: 700, chest: 1200 } as const;
/** The chest block's rise (end.css chest-rise), after END_T.chest: the guide waits for it to settle before pointing. */
const CHEST_RISE_MS = 450;

/** The run's report on the end screen (WP2): the top towers by damage (QoL R2) and the share card (U19). */
export interface EndReport {
  readonly towers: readonly TowerDamageRow[];
  /** Shares the end card (ui/shareCard.ts); null hides SHARE (no share sheet or clipboard here). */
  readonly share: (() => Promise<ShareOutcome>) | null;
}

/** What the level knows for Captain Bram's end-screen lines (DESIGN §6.3; level/levelController.ts fills it). */
export interface EndGuideFacts {
  /** This win is the save's first clear of any level. */
  readonly firstWin: boolean;
  /** This loss is the save's first loss (as near as the level can tell: a loss on a level never cleared). */
  readonly firstLoss: boolean;
  readonly highestCleared: number;
}

/** One end-screen star: the slot carries its slam delay and, when earned, the spark burst pseudo-elements. */
function endStar(earned: boolean, i: number, size: number): string {
  const svg = icon("star", size, undefined, 2);
  return `<span class="slot s${i} ${earned ? "earned" : "off"}" style="--d:${END_T.stars[i]}ms"><span class="star ${earned ? "" : "off"}">${earned ? svg.replace('fill="none"', 'fill="currentColor"') : svg}</span></span>`;
}

/**
 * The end screen's choices, with one clear primary (Phase 2: the old RETRY / HUB / NEXT LEVEL row was three equal
 * buttons squeezed into 330 px, the primary blue among them). The primary is the full-width 60 px button — NEXT
 * LEVEL (with the next level's name) after a win, RETRY after a loss, CONTINUE after the last level — and the rest sit
 * under it as smaller dark buttons.
 *
 * One gold per view (UI design system v2, rule 3): while the battle chest waits on the screen, its OPEN is the gold
 * action and the primary waits in blue; opening the chest hands the gold to the primary (`promotePrimary`).
 */
function choicesHtml(sim: LevelSim, won: boolean, hasNext: boolean, share: boolean, chestWaiting: boolean): string {
  // MP-21: an event mode's own next run (Boss Week's next tier, the Odyssey's next map) or the hub
  if (sim.isEventRun) return eventChoicesHtml(sim, won, chestWaiting ? "blue" : "gold", features.nextEventRun(sim.modifiers, won) !== null);
  if (sim.isIron || sim.isEndless || sim.isDaily || sim.isChallenge) return modeChoicesHtml(sim, won, share, chestWaiting);
  let nextName = "";
  try { nextName = hasNext ? sim.balance.level(sim.levelNumber + 1).name : ""; } catch { /* no such level */ }
  const tone = chestWaiting ? "blue" : "gold";
  const primary = won && hasNext
    ? btnHtml("NEXT LEVEL", tone, { icon: "play", data: `data-c="next"`, className: "disp accent primary", sub: nextName ? `Level ${sim.levelNumber + 1} · ${nextName}` : undefined })
    : won
      ? btnHtml("CONTINUE", tone, { icon: "play", data: `data-c="levels"`, className: "disp accent primary" })
      : btnHtml("RETRY", tone, { icon: "rotate", data: `data-c="retry"`, className: "disp accent primary" });
  const secondary = won
    ? `${btnHtml("RETRY", "dark", { icon: "rotate", data: `data-c="retry"`, className: "disp" })}${hasNext ? btnHtml("HUB", "dark", { icon: "map", data: `data-c="levels"`, className: "disp" }) : ""}${share ? btnHtml("SHARE", "dark", { icon: "star", className: "disp share" }) : ""}`
    : btnHtml("HUB", "dark", { icon: "map", data: `data-c="levels"`, className: "disp" });
  return `<div class="choices">${primary}<div class="row secondary">${secondary}</div></div>`;
}

/**
 * Iron and Endless (docs/MODES.md): Iron has one try a day, so no RETRY and no NEXT — CONTINUE back to the hub; Endless
 * offers PLAY AGAIN (a fresh run) over HUB. Neither has a checkpoint retry.
 */
function modeChoicesHtml(sim: LevelSim, won: boolean, share: boolean, chestWaiting: boolean): string {
  const tone = chestWaiting ? "blue" : "gold";
  const shareBtn = share ? btnHtml("SHARE", "dark", { icon: "star", className: "disp share" }) : "";
  // MP-20: Daily Road — PLAY AGAIN is today's road (practice once the day is paid); SHARE after a loss too (the score).
  // MP-28: a challenge code's run the same (PLAY AGAIN is the same code).
  if (sim.isDaily || sim.isChallenge) {
    const again = btnHtml("PLAY AGAIN", won ? "dark" : tone, { icon: "rotate", data: `data-c="retry"`, className: `disp${won ? "" : " accent primary"}`, sub: won ? undefined : sim.isChallenge ? "The same challenge" : "Today's road again" });
    return won ? `<div class="choices">${btnHtml("CONTINUE", tone, { icon: "play", data: `data-c="levels"`, className: "disp accent primary" })}<div class="row secondary">${again}${shareBtn}</div></div>`
      : `<div class="choices">${again}<div class="row secondary">${btnHtml("HUB", "dark", { icon: "map", data: `data-c="levels"`, className: "disp" })}${shareBtn}</div></div>`;
  }
  if (sim.isIron) {
    return `<div class="choices">${btnHtml("CONTINUE", tone, { icon: "play", data: `data-c="levels"`, className: "disp accent primary", sub: won ? undefined : "Another try tomorrow" })}${shareBtn ? `<div class="row secondary">${shareBtn}</div>` : ""}</div>`;
  }
  return `<div class="choices">${btnHtml("PLAY AGAIN", tone, { icon: "rotate", data: `data-c="retry"`, className: "disp accent primary", sub: "A new Endless run" })}<div class="row secondary">${btnHtml("HUB", "dark", { icon: "map", data: `data-c="levels"`, className: "disp" })}</div></div>`;
}

/** The end screen's four stat plates: label, value, and how a counting value reads (stageLevelEnd counts them up). */
function endStats(sim: LevelSim): [string, number, (v: number) => string][] {
  if (sim.isEndless) return [["WAVES", sim.stagesCleared, String], ["KILLS", sim.kills, String], ["POINTS", sim.points, (v) => fmtCompact(v)], ["LEAKS", sim.leaks, String]];
  if (sim.isIron) {
    // How far through the one long wave: the share of its spawns out of the portal (all of it on a win).
    const pct = sim.phase === LevelPhase.Won ? 100 : Math.floor((100 * sim.spawnedThisStage) / Math.max(1, sim.scheduledThisStage));
    return [["WAVE", pct, (v) => `${v}%`], ["KILLS", sim.kills, String], ["LEAKS", sim.leaks, String], ["FORTRESS", sim.baseHp, (v) => `${v}/${sim.maxBaseHp}`]];
  }
  return [["WAVES", sim.stagesCleared, (v) => `${v}/${sim.stages}`], ["KILLS", sim.kills, String], ["LEAKS", sim.leaks, String], ["FORTRESS", sim.baseHp, (v) => `${v}/${sim.maxBaseHp}`]];
}

/** Daily Road's and Endless's painted icon on their end-screen crest (CSS px). */
const DAILY_CREST_PX = 56;
/** The Iron crest: the iron-bound star shield alone (lit on a win, greyed on a loss: modes.css). */
const IRON_CREST_PX = 72;

/** The crest's centre for a mode run: Endless shows the waves survived, Iron its one star (lit on a win), Daily Road its score. */
function modeCrestHtml(sim: LevelSim, won: boolean): string {
  if (sim.isChallenge) return challengeCrestHtml(sim, won);
  if (sim.isEventRun) return eventCrestHtml(sim, won);
  if (sim.isDaily) return `<div class="modecrest daily${won ? " earned" : ""}"><span class="ic">${paintedIconHtml("dailyRoad", DAILY_CREST_PX)}</span><b class="disp">${fmtCompact(dailyScore(sim.balance.modes.daily, { won, stagesCleared: sim.stagesCleared, baseHp: won ? sim.baseHp : 0 }))}</b><small>score</small></div>`;
  if (sim.isEndless) return `<div class="modecrest endless"><span class="ic">${paintedIconHtml("endless", DAILY_CREST_PX)}</span><b class="disp">WAVE ${sim.stagesCleared}</b><small>survived</small></div>`;
  return `<div class="modecrest iron${won ? " earned" : ""}"><span class="ic">${paintedIconHtml("iron", IRON_CREST_PX)}</span></div>`;
}

/** The chest is open: its OPEN is gone, so the choice primary takes the screen's gold (see choicesHtml). */
export function promotePrimary(end: HTMLElement): void {
  end.querySelector(".choices .btn.primary")?.classList.replace("blue", "gold");
}

/** Level end (P1.10.7, mockup 07): stars over a VICTORY/DEFEAT ribbon, a parchment panel with four stats, the chest block and the choices. */
export function showLevelEnd(parent: HTMLElement, sim: LevelSim, nextUnlocked: boolean, onChoice: (c: LevelEndChoice) => void, openChest: ChestOpener | null = null, endStars: EndStars | null = null, defeatHtml = "", guideFacts: EndGuideFacts | null = null, report: EndReport | null = null): HTMLElement {
  const won = sim.phase === LevelPhase.Won;
  const el = document.createElement("div");
  const mode = sim.isEndless ? "endless" : sim.isIron ? "iron" : sim.isDaily ? "daily" : sim.isChallenge ? "challenge" : sim.isEventRun ? "evrun" : "";
  el.className = `end ${won ? "won" : "lost"}${mode ? ` mode ${mode}` : ""}`;
  const chest = sim.chest;
  const rarity = chest ? RARITY_IDS[chest.rarity] : "";
  const conds = starConditions(sim.balance, sim.levelNumber);
  const run = won ? endStars?.run ?? 0 : 0;
  const earned = conds.map((c) => (run & c.bit) !== 0);
  const nextChoice = won && nextUnlocked ? "next" : "levels";
  const facts: EndScreenFacts = { level: sim.levelNumber, won, stars: run, sim };
  const extras = features.endScreenExtras().map((x) => ({ x, html: x.html(facts) })).filter((e) => e.html !== "");
  el.innerHTML = `
    <div class="crest">
      ${won ? `<div class="rays"></div>` : ""}
      ${mode ? modeCrestHtml(sim, won) : `<div class="stars">${endStar(earned[0], 0, 44)}${endStar(earned[1], 1, 56)}${endStar(earned[2], 2, 44)}</div>`}
      ${mode === "endless" ? ribbonHtml("RUN OVER", "", "disp") : ribbonHtml(won ? (mode === "iron" ? "IRON VICTORY" : "VICTORY") : "DEFEAT", won ? "" : "red", "disp")}
      ${won && endStars && !mode ? newBestHtml(endStars) : ""}
    </div>
    <div class="panel">
      <div class="stats4">${endStats(sim).map(([label, v, fmt]) => statHtml(label, fmt(v))).join("")}</div>
      ${won && endStars && !mode ? starCondsHtml(conds, run, endStars.before) : ""}
      ${won ? "" : defeatHtml /* defeatPanel.ts: the defeat screen's lesson (keep.css hides the four stats under it) */}
      ${won && report ? topTowersHtml(report.towers) : ""}
      ${chest ? `<div class="chest common mystery"><img class="glow" decoding="sync" src="${uiUrl("chests/glow_common.png")}" alt=""><img class="lid" decoding="sync" src="${uiUrl("chests/chest_common_closed.png")}" alt=""><b class="rname disp">BATTLE CHEST</b>${btnHtml("OPEN", "gold", { className: "open disp" })}</div>` : ""}
      ${extras.length > 0 ? `<div class="end-extras">${extras.map((e) => `<div class="end-extra" data-extra="${e.x.id}">${e.html}</div>`).join("")}</div>` : ""}
      ${choicesHtml(sim, won, nextChoice === "next", (won || sim.isDaily || sim.isChallenge) && report?.share != null, chest !== null)}
    </div>`;
  for (const e of extras) { const block = el.querySelector<HTMLElement>(`.end-extra[data-extra="${e.x.id}"]`); if (block) e.x.bind?.(block, facts); }
  el.querySelectorAll<HTMLButtonElement>("button[data-c]").forEach((b) => (b.onclick = () => onChoice(b.dataset.c as LevelEndChoice)));
  const share = report?.share;
  // share() reaches the share sheet in the tap's own call stack (Safari needs the user gesture); the picture is ready
  const shareBtn = el.querySelector<HTMLButtonElement>(".share");
  if (shareBtn && share) shareBtn.onclick = () => { void share().then((r) => { if (r === "copied") toast(parent, "Copied — paste it anywhere"); else if (r === "failed") toast(parent, "Can't share from here"); }); };
  let chestOpened = false;
  const signal = guideFacts ? attachEndGuide(el, sim, won, guideFacts, () => chestOpened) : () => undefined;
  const openBtn = el.querySelector<HTMLButtonElement>(".open");
  if (openBtn && chest) {
    // The HQ chest model rattles in place of the painted card while the player decides (art/CHEST_HANDOFF.md); the
    // 2D lid stays as the fallback when the library is missing.
    const block = el.querySelector<HTMLElement>(".chest")!;
    let stage: ChestStage | null = null;
    let opened = false;
    const mount = () => {
      stage = new ChestStage(rarity, 300, 200);
      const well = document.createElement("div"); well.className = "well";
      well.append(block.querySelector("img.glow")!, stage.canvas);
      block.querySelector("img.lid")!.replaceWith(well);
    };
    // M33: the 3D chest is a WebGL context of its own; it is made once the level behind the end screen has rested
    // (SceneRig.rest gives back its frame's targets: the two never hold their memory at once), before the chest block
    // rises at END_T.chest. Until then — and for good if it never loads — the painted lid stands in.
    const mountWhenRested = () => { mainRest.whenResting(() => { if (!opened && !stage && el.isConnected && ChestStage.available(rarity)) mount(); }, END_T.chest); };
    if (ChestStage.available(rarity)) mountWhenRested();
    // Chest models load per use (render/assetNeeds.ts), not with the level: the painted lid shows until this one is in.
    else void lib()?.ensure(ChestStage.modelsFor(rarity)).then(() => { if (!opened && el.isConnected && ChestStage.available(rarity)) mountWhenRested(); });
    // the chest itself is a tap target too (UI pass 2026-10-21): tapping the rattling chest opens it like OPEN
    block.addEventListener("click", (e) => { if (!opened && (e.target as HTMLElement).closest(".well, img.lid")) openBtn.click(); });
    openBtn.onclick = () => {
      opened = true;
      chestOpened = true;
      promotePrimary(el);
      signal("chestOpened");
      const gear = openChest ? openChest() : [];
      chestReveal(block, sim.balance, chest.rarity, { gear, speedUpMinutes: chest.speedUpMinutes, gold: chest.gold, essence: chestEssence(sim.balance, chest) }, () => undefined, stage);
    };
  }
  parent.append(el);
  stageLevelEnd(el, sim, earned);
  return el;
}

/**
 * The end screen as Captain Bram's host (DESIGN §6.3): attached while the screen is up, fed its facts every frame, and
 * detached when the screen goes. The guide waits for the stars and the chest to land before it points. Returns the
 * screen's signal function.
 */
function attachEndGuide(el: HTMLElement, sim: LevelSim, won: boolean, facts: EndGuideFacts, chestOpened: () => boolean): (s: GuideSignal) => void {
  const g = guide();
  if (g === null) return () => undefined;
  const shownAt = performance.now(), settleMs = motionOk() ? END_T.chest + CHEST_RISE_MS : 0;
  let pending = new Set<GuideSignal>();
  g.attach("end", { root: el, world: null, busy: () => (performance.now() - shownAt < settleMs ? "loading" : null) });
  const frame = () => {
    if (!el.isConnected) { g.detach("end"); return; }
    const signals = pending;
    pending = new Set();
    g.update({ screen: "end", level: null, hub: null, highestCleared: facts.highestCleared, signals,
      end: { level: sim.levelNumber, won, chestOpened: chestOpened(), firstWin: won && facts.firstWin, firstLoss: !won && facts.firstLoss } });
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  return (s) => { pending.add(s); g.signal(s); };
}

/** Victory on a replay that raised the level's star count: a "NEW BEST 2→3★" stamp on the ribbon's corner. */
function newBestHtml(s: EndStars): string {
  const before = starCount(s.before), after = starCount(s.before | s.run);
  return s.before !== 0 && after > before ? `<b class="newbest disp">NEW BEST ${before}→${after}★</b>` : "";
}

/** Victory: the three conditions under the stats, each lit when this run met it (NEW on one met for the first time). */
/** Star glyph size in the victory condition row. */
const COND_STAR_PX = 13;

function starCondsHtml(conds: ReturnType<typeof starConditions>, run: StarMask, before: StarMask): string {
  const svg = icon("star", COND_STAR_PX, undefined, 2);
  const chips = conds.map((c) => {
    const on = (run & c.bit) !== 0;
    const fresh = on && before !== 0 && (before & c.bit) === 0;
    return `<span class="cond${on ? " on" : ""}">${on ? svg.replace('fill="none"', 'fill="currentColor"') : svg}<i>${c.text}</i>${fresh ? `<em>NEW</em>` : ""}</span>`;
  }).join("");
  return `<div class="starconds">${chips}</div>`;
}

/**
 * Runs the JS half of the F5 timeline (the CSS half is the keyframes in end.css): a 3 px, 120 ms shake of the whole
 * screen as each earned star lands, and the stat plates counting up. Timers stop if the screen is torn down early.
 */
function stageLevelEnd(el: HTMLElement, sim: LevelSim, earned: readonly boolean[]): void {
  if (!motionOk()) return;
  const later = (ms: number, fn: () => void) => window.setTimeout(() => { if (el.isConnected) fn(); }, ms);
  for (let i = 0; i < earned.length; i++) {
    if (!earned[i]) continue;
    // Lands at ~55 % of the 320 ms slam, when the star hits scale 1.
    later(END_T.stars[i] + 175, () => el.animate([{ transform: "translate(0,0)" }, { transform: "translate(-3px,2px)" }, { transform: "translate(3px,-2px)" }, { transform: "translate(-2px,-1px)" }, { transform: "translate(0,0)" }], { duration: 120, easing: "linear" }));
  }
  const plates = [...el.querySelectorAll<HTMLElement>(".stats4 .stat b")];
  const values: [number, (v: number) => string][] = endStats(sim).map(([, v, fmt]) => [v, fmt]);
  plates.forEach((b, i) => { b.textContent = values[i][1](0); });
  later(END_T.stats, () => plates.forEach((b, i) => { const [v, fmt] = values[i]; tickNumber(b, 0, v, END_T.statsMs, fmt); }));
}

/**
 * The run's verdict under the VICTORY ribbon (level/moments.ts HpHistory.verdict): "CLUTCH VICTORY" or "COMEBACK".
 * Added by the level after showLevelEnd, so the end screen's own layout is untouched when there is none.
 */
export function endTag(end: HTMLElement, text: string, kind: string): void {
  const crest = end.querySelector(".crest");
  if (!crest) return;
  const tag = document.createElement("div");
  tag.className = `end-tag disp ${kind}`;
  tag.textContent = text;
  crest.append(tag);
}
